<p-overlayPanel #op styleClass="syntax-hint-panel" appendTo="body">
    <ng-template pTemplate>
        <!--Queries-->
        <h3>Query Insert Syntax</h3>
        <div>
            <p>
                This syntax allows you to embed values from SQL queries directly into
                your HTML content.
                This is particularly useful in reports where information needs to be displayed based on database values.
            </p>
            <grid [tableSettings]="querySyntaxTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <h4>Components of the Syntax</h4>
            <grid [tableSettings]="querySyntaxComponentsTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <br/>
            <p-panel header="How It Works" toggleable="true" collapsed="true" toggler="header">
                <p>
                    When the HTML is processed, the application recognizes this special syntax and replaces it with the
                    corresponding value fetched from the SQL query. This allows for seamless integration of database
                    values into your web pages.
                </p>

                <h4>Examples 1: Inserting User Name</h4>
                <p>
                    Assume you have a SQL query named <b>getUserDetails</b> that retrieves user information,
                    including a column called username.
                </p>
                <p>
                    <b>SQL Query Definition</b><br/>
                    <code>SELECT username FROM users WHERE user_id = 1;</code>
                    <br/>
                    <b>HTML Usage</b><br/>
                    <code>&lt;p&gt;Welcome, &#64;query(getUserDetails, username)!&lt;/p&gt;</code>
                    <br/>
                    <b>Output</b><br/>
                    If the username retrieved from the query is "JohnDoe", the resulting HTML will display:<br/>
                    <code>&lt;p&gt;Welcome, JohnDoe!&lt;/p&gt;</code>
                </p>

                <h4>Examples 2: Displaying Product Price</h4>
                <p>
                    Consider you have a query named <b>getProductPriceInfo</b> that retrieves details about a product,
                    including its price in a column called price.
                </p>
                <p>
                    <b>SQL Query Definition</b><br/>
                    <code>SELECT price FROM products WHERE product_id = 1001;</code>
                    <br/>
                    <b>HTML Usage</b><br/>
                    <code>&lt;p&gt;The price of the product is $&#64;query(getProductPriceInfo).&lt;/p&gt;</code>
                    <br/>
                    <b>Output</b><br/>
                    If the price retrieved from the query is "29.99", the resulting HTML will display:
                    <br/>
                    <code>&lt;p&gt;The price of the product is $29.99.&lt;/p&gt;</code>
                </p>

                <h4>Important Notes</h4>
                <ul>
                    <li>
                        Query Definitions: Ensure that the queries are defined in your application before using
                        the syntax in your HTML.
                    </li>
                    <li>Error Handling: Be mindful of potential errors, such as if the query results in no data.</li>
                </ul>
                By using this syntax, you can create dynamic and data-driven web pages that provide users with real-time
                information from your database.
            </p-panel>
        </div>

        <!--Widgets-->
        <br/>
        <br/>
        <h3>Widget Insert Syntax</h3>
        <div>
            <p>
                The special syntax you are using allows you to dynamically insert a named widget instance directly into
                your HTML content.
                This is particularly useful in reports to create specific/complex layout based on pure HTML which
                contains inserted of named widgets.
            </p>
            <grid [tableSettings]="widgetSyntaxTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <h4>Components of the Syntax</h4>
            <grid [tableSettings]="widgetSyntaxComponentsTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <br/>

            <p-panel header="How It Works" toggleable="true" collapsed="true" toggler="header">
                <p>
                    When the HTML is processed, the application recognizes this special syntax and replaces it with the
                    corresponding widget component instance. An inserted widget should be named and have widget code
                    defined in builder settings.
                </p>

                <h4>Example: Inserting a Chart Widget Showing Blood Pressure Series</h4>
                <p>Assume you already have a chart widget named "Blood pressure by days" with widget code
                    "chart_bp_by_days" defined.</p>
                <p>You can make insert into the HTML widget using the following code:</p>
                <code>... See the blood pressure chart below: &#64;widget(chart_bp_by_days)</code>
            </p-panel>
        </div>

        <!--Expressions-->
        <br/>
        <br/>
        <h3>Expression Insert Syntax</h3>
        <div>
            <p>
                This syntax allows you to embed and evaluate mathematical
                expressions using the <a href="https://mathjs.org/examples/index.html" target="_blank">math.js
                library</a> directly within your report widgets.
                This enables dynamic calculations based on emitted variable values and seamlessly inserts the results
                into your HTML
                content.
            </p>
            <grid [tableSettings]="expressionSyntaxTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <h4>Components of the Syntax</h4>
            <grid [tableSettings]="expressionSyntaxComponentsTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <br/>

            <p-panel header="How It Works" toggleable="true" collapsed="true" toggler="header">
                <p>
                    When the HTML is processed, the application recognizes this special syntax and replaces it with the
                    corresponding value evaluated from the expression.
                </p>

                <h4>Example 1: Using Basic Arithmetic Functions</h4>
                <p>In this example, we will calculate the total score by adding the scores of two assessments. Assume we
                    have two variables: <code>#score1</code> and <code>#score2</code>.</p>
                <p>You can calculate the total score using the following code:</p>
                <code>&#64;eval(#score1 + #score2)</code>
                <p>This code will use the math.js library to add the values of <code>#score1</code> and
                    <code>#score2</code> and insert the result into the content.</p>

                <h4>Example 2: IF/ELSE Operator</h4>
                <p>In this scenario, we want to show/hide HTML element depending on received boolean variable. Let's say
                    this variable is emitted when a data row of grid widget is selected and the row has Confirmed
                    field:</p>
                <p>Use the following code:</p>
                <code>&lt;div style=&quot;display:<strong>&#64;eval(#confirmed ? &#39;inherited&#39; :
                    &#39;none&#39;)</strong>&quot;&gt;Confirmed!&lt;/div&gt;</code>
                <p>This expression leverages the math.js library to evaluate the conditional expression and dynamically
                    insert the resulting value into the content.</p>
            </p-panel>
        </div>

        <!--For Loop-->
        <br/>
        <br/>
        <h3>For Loop Syntax</h3>
        <div>
            <p>
                This syntax allows you to iterate over a list of values and repeat HTML content for each item.
                This is particularly useful for displaying lists, tables, or any repeating content based on query
                results or other data sources.
            </p>
            <grid [tableSettings]="forLoopSyntaxTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <h4>Components of the Syntax</h4>
            <grid [tableSettings]="forLoopSyntaxComponentsTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <br/>

            <p-panel header="How It Works" toggleable="true" collapsed="true" toggler="header">
                <p>
                    When the HTML is processed, the application recognizes this special syntax and repeats the body
                    block
                    for each item in the specified list. You can use the loop variable and optional identifiers within
                    the body.
                </p>

                <h4>Example 1: Basic List Iteration</h4>
                <p>Assume you have a query that returns a list of user names.</p>
                <p>You can iterate over the results using the following code:</p>
                <code>&#64;for(user of &#64;query(getUsers)) &#123; &lt;li&gt;&#64;user&lt;/li&gt; &#125;</code>
                <p>This will create a list item for each user returned by the query.</p>

                <h4>Example 2: Using Loop Identifiers</h4>
                <p>You can use special identifiers like $index, $count, $first, $last, $even, and $odd to add
                    conditional logic or numbering.</p>
                <p>Use the following code:</p>
                <code>&#64;for(item of &#64;query(getItems); idx = $index; total = $count) &#123; &lt;p&gt;Item &#64;idx
                    of &#64;total: &#64;item&lt;/p&gt; &#125;</code>
                <p>This will display each item with its position and total count.</p>

                <h4>Available Loop Identifiers</h4>
                <ul>
                    <li><code>$index</code> - The zero-based index of the current iteration</li>
                    <li><code>$count</code> - The total number of items in the list</li>
                    <li><code>$first</code> - Boolean indicating if this is the first item</li>
                    <li><code>$last</code> - Boolean indicating if this is the last item</li>
                    <li><code>$even</code> - Boolean indicating if the index is even</li>
                    <li><code>$odd</code> - Boolean indicating if the index is odd</li>
                </ul>
            </p-panel>
        </div>

        <!--Range-->
        <br/>
        <br/>
        <h3>Range Syntax</h3>
        <div>
            <p>
                This syntax allows you to generate a list of sequential numbers. This is particularly useful when
                combined
                with the &#64;for directive to iterate a specific number of times or to generate numbered lists.
            </p>
            <grid [tableSettings]="rangeSyntaxTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <h4>Components of the Syntax</h4>
            <grid [tableSettings]="rangeSyntaxComponentsTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <br/>

            <p-panel header="How It Works" toggleable="true" collapsed="true" toggler="header">
                <p>
                    When the HTML is processed, the application recognizes this special syntax and generates a JSON
                    array
                    of sequential numbers. This array can be used directly or combined with the &#64;for directive to
                    iterate
                    a specific number of times.
                </p>

                <h4>Example 1: Generating a Simple Range</h4>
                <p>To generate a list of numbers from 0 to 4:</p>
                <code>&#64;range(5)</code>
                <p>This will generate the array: <code>[0, 1, 2, 3, 4]</code></p>

                <h4>Example 2: Generating a Range with Start and End</h4>
                <p>To generate a list of numbers from 2 to 4:</p>
                <code>&#64;range(2, 5)</code>
                <p>This will generate the array: <code>[2, 3, 4]</code></p>

                <h4>Example 3: Using Range with For Loop</h4>
                <p>To repeat content a specific number of times, you can combine &#64;range with &#64;for:</p>
                <code>&#64;for(i of &#64;range(3)) &#123; &lt;p&gt;Iteration &#64;i&lt;/p&gt; &#125;</code>
                <p>This will create three paragraphs: "Iteration 0", "Iteration 1", and "Iteration 2".</p>

                <h4>Example 4: Creating Numbered Lists</h4>
                <p>You can use &#64;range to create numbered content:</p>
                <code>&#64;for(n of &#64;range(1, 6)) &#123; &lt;li&gt;Item &#64;n&lt;/li&gt; &#125;</code>
                <p>This will create list items numbered from 1 to 5.</p>

                <h4>Important Notes</h4>
                <ul>
                    <li>
                        Single Argument: When only one argument is provided, the range starts at 0 and goes up to
                        (but not including) the specified count.
                    </li>
                    <li>
                        Two Arguments: When two arguments are provided, the range starts at the first argument and goes
                        up to (but not including) the second argument.
                    </li>
                    <li>
                        JSON Format: The directive returns a JSON stringified array, which can be parsed and used with
                        the &#64;for directive.
                    </li>
                </ul>
            </p-panel>
        </div>

        <!--Type Conversion-->
        <br/>
        <br/>
        <h3>Type Conversion Syntax</h3>
        <div>
            <p>
                This syntax allows you to convert values between different types (string, number, boolean).
                This is particularly useful when you need to ensure a value is in a specific format for display or
                calculation.
            </p>
            <grid [tableSettings]="typeConversionSyntaxTableSettings" [gridSettings]="syntaxGridSettings"></grid>
            <h4>Components of the Syntax</h4>
            <grid [tableSettings]="typeConversionSyntaxComponentsTableSettings"
                  [gridSettings]="syntaxGridSettings"></grid>
            <br/>

            <p-panel header="How It Works" toggleable="true" collapsed="true" toggler="header">
                <p>
                    When the HTML is processed, the application recognizes these directives and converts the value
                    to the specified type. If conversion fails, default values are returned (empty string for string, 0
                    for number, false for boolean).
                </p>

                <h4>Example 1: Converting to String</h4>
                <p>If you have a numeric value from a query and want to ensure it's displayed as a string:</p>
                <code>&#64;string(&#64;query(getPrice))</code>
                <p>This will convert the price value to a string representation, wrapped in quotes.</p>

                <h4>Example 2: Converting to Number</h4>
                <p>If you have a string value that represents a number and need to perform calculations:</p>
                <code>&#64;eval(&#64;number(&#64;query(getCount)) * 2)</code>
                <p>This will convert the count to a number and then multiply it by 2.</p>

                <h4>Example 3: Converting to Boolean</h4>
                <p>If you need to check a value as a boolean in a conditional expression:</p>
                <code>&#64;eval(&#64;bool(&#64;query(getIsActive)) ? 'Active' : 'Inactive')</code>
                <p>This will convert the value to a boolean and use it in a conditional expression.</p>
            </p-panel>
        </div>

    </ng-template>
</p-overlayPanel>

<i class="tooltip-icon material-icons" pTooltip="Click for hint"
   tooltipEvent="hover" appendTo="body" tabindex="0"
   (click)="op.toggle($event)">info</i>
